// features/projects/templates/continuum/cascade.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM CASCADE INTEGRATION — Tab 9
//
// Read-only viewer surfacing the cascade integration plan + the
// 19-hypothesis registry (H-1 through H-18 + H-3v3) as live UI
// inside the Continuum project. Source-of-truth is the docs at
// docs/CONTINUUM_CASCADE_INTEGRATION.md and
// docs/CONTINUUM_HYPOTHESIS_FORMAT.md — this view renders the
// structured data extracted from those docs.
//
// v1 sections (Session 1):
//   1. Phase Status Strip — Phase 0-7 progress
//   2. Hypothesis Registry table — all 19 hypotheses with status
//      badges; click to expand for full claim + falsifier
//
// Coming sessions:
//   3. 17-dim Framework browser (Session 2)
//   4. Intervention Grids viewer (Session 3)
//   5. Phase A Verdicts feed + Next Moves panel (Session 4)
//
// Read-only v1; migrate to DB-backed editability if Phase 6/7 is
// greenlit.
// ═══════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { C, FONT as F } from './tokens';
import { PHASES, STATUS_COLOR as PHASE_STATUS_COLOR, STATUS_LABEL as PHASE_STATUS_LABEL } from '../../../../lib/cascade/phases';
import {
  HYPOTHESES,
  GROUP_LABEL,
  STATUS_COLOR as HYP_STATUS_COLOR,
  PHASE_A_LABEL,
  getCounts,
} from '../../../../lib/cascade/hypotheses';
import CascadeFrameworks from './cascade-frameworks';
import CascadeGrids from './cascade-grids';
import CascadeVerdicts from './cascade-verdicts';

export default function ContinuumCascade({ workspaceId, userId }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'auto',
        background: C.bg,
        color: C.text,
        fontFamily: F.sans,
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 40px 80px' }}>
        <Header />
        <PhaseStatusStrip />
        <HypothesisRegistry />
        <CascadeFrameworks />
        <CascadeGrids />
        <CascadeVerdicts />
      </div>
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────────────────
function Header() {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 10, color: C.static, fontFamily: F.mono, letterSpacing: '0.1em', marginBottom: 8 }}>
        CASCADE INTEGRATION · LIVE STATUS · READ-ONLY V1
      </div>
      <div style={{ fontFamily: F.serif, fontSize: 36, fontWeight: 300, lineHeight: 1.1, marginBottom: 12 }}>
        The Cascade Integration Plan
      </div>
      <div style={{ fontSize: 14, color: C.mid, lineHeight: 1.6, maxWidth: 800 }}>
        Substrate-symmetric methodology unifying eco and human cascade analysis under a single instrument.
        Source-of-truth: <code style={{ color: C.gold, fontFamily: F.mono, fontSize: 12 }}>docs/CONTINUUM_CASCADE_INTEGRATION.md</code> + <code style={{ color: C.gold, fontFamily: F.mono, fontSize: 12 }}>docs/CONTINUUM_HYPOTHESIS_FORMAT.md</code>.
      </div>
    </div>
  );
}

// ─── Phase Status Strip ──────────────────────────────────────────────
function PhaseStatusStrip() {
  return (
    <section style={{ marginBottom: 48 }}>
      <SectionHeading number="01" label="Phase Status" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {PHASES.map((phase) => (
          <PhaseRow key={phase.id} phase={phase} />
        ))}
      </div>
    </section>
  );
}

function PhaseRow({ phase }) {
  const colorKey = PHASE_STATUS_COLOR[phase.status] || 'dim';
  const color = C[colorKey];
  const isComplete = phase.status === 'complete' || phase.status === 'first-pass-complete';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '90px 1fr 130px',
        alignItems: 'flex-start',
        gap: 16,
        padding: '14px 16px',
        background: C.s2,
        border: `1px solid ${C.b1}`,
        borderRadius: 4,
      }}
    >
      <div>
        <div style={{ fontSize: 11, color: C.static, fontFamily: F.mono, letterSpacing: '0.06em' }}>{phase.label}</div>
        {phase.completedDate && (
          <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginTop: 2 }}>{phase.completedDate}</div>
        )}
      </div>
      <div>
        <div style={{ fontSize: 14, fontFamily: F.serif, fontWeight: 400, color: C.text, marginBottom: 4 }}>
          {phase.title}
        </div>
        <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.5 }}>
          {phase.summary}
        </div>
        {phase.killSwitchVerdict && (
          <div style={{ fontSize: 11, color: C.static, lineHeight: 1.5, marginTop: 6, fontStyle: 'italic' }}>
            <span style={{ color: C.dim, marginRight: 6 }}>Verdict:</span>
            {phase.killSwitchVerdict}
          </div>
        )}
        {phase.artifact && (
          <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginTop: 6 }}>
            ↪ {phase.artifact}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <StatusBadge color={color} label={PHASE_STATUS_LABEL[phase.status]} filled={isComplete} />
      </div>
    </div>
  );
}

// ─── Hypothesis Registry ─────────────────────────────────────────────
function HypothesisRegistry() {
  const [expandedId, setExpandedId] = useState(null);
  const [groupFilter, setGroupFilter] = useState('all');
  const counts = useMemo(() => getCounts(), []);

  const filtered = useMemo(() => {
    if (groupFilter === 'all') return HYPOTHESES;
    return HYPOTHESES.filter((h) => h.group === groupFilter);
  }, [groupFilter]);

  return (
    <section style={{ marginBottom: 48 }}>
      <SectionHeading number="02" label="Hypothesis Registry" sublabel={`${counts.total} hypotheses · ${counts.active} active · ${counts.confirmed} confirmed · ${counts.retired} retired · ${counts.proposed} proposed`} />

      <FilterStrip value={groupFilter} onChange={setGroupFilter} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filtered.map((h) => (
          <HypothesisRow
            key={h.id}
            hypothesis={h}
            expanded={expandedId === h.id}
            onToggle={() => setExpandedId((cur) => (cur === h.id ? null : h.id))}
          />
        ))}
      </div>
    </section>
  );
}

function FilterStrip({ value, onChange }) {
  const options = [
    { id: 'all', label: 'All' },
    { id: 'retrofitted', label: 'Retrofitted (pilots)' },
    { id: 'tier-1-forward', label: 'Tier 1 forward' },
    { id: 'cascade-integration', label: 'Cascade integration' },
    { id: 'cross-substrate-transfer', label: 'Cross-substrate transfers' },
  ];
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            style={{
              fontSize: 10,
              fontFamily: F.mono,
              letterSpacing: '0.06em',
              padding: '4px 10px',
              borderRadius: 3,
              border: `1px solid ${active ? C.gold : C.b2}`,
              background: active ? hexAlpha(C.gold, 0.08) : 'transparent',
              color: active ? C.gold : C.static,
              cursor: 'pointer',
              textTransform: 'uppercase',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function HypothesisRow({ hypothesis: h, expanded, onToggle }) {
  const colorKey = HYP_STATUS_COLOR[h.status] || 'static';
  const color = C[colorKey] || C.static;
  const isKeystone = h.keystone;

  return (
    <div
      style={{
        background: C.s2,
        border: `1px solid ${isKeystone ? C.gold : C.b1}`,
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr 130px 130px',
          alignItems: 'center',
          gap: 16,
          padding: '12px 16px',
          width: '100%',
          background: 'transparent',
          border: 'none',
          color: C.text,
          textAlign: 'left',
          cursor: 'pointer',
          fontFamily: F.sans,
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: isKeystone ? C.gold : C.mid, fontFamily: F.mono, letterSpacing: '0.04em' }}>
            {h.id}
            {isKeystone && <span style={{ color: C.gold, marginLeft: 6 }}>★ KEYSTONE</span>}
          </div>
        </div>
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.4 }}>
          {h.title}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <StatusBadge color={color} label={h.status.toUpperCase()} filled={h.status === 'confirmed' || h.status === 'active'} />
        </div>
        <div style={{ fontSize: 10, color: C.static, fontFamily: F.mono, textAlign: 'right' }}>
          {PHASE_A_LABEL[h.phaseAStatus] || h.phaseAStatus}
        </div>
      </button>

      {expanded && <HypothesisDetail hypothesis={h} />}
    </div>
  );
}

function HypothesisDetail({ hypothesis: h }) {
  return (
    <div style={{ borderTop: `1px solid ${C.b1}`, padding: '16px 20px', background: C.s1 }}>
      <DetailField label="Claim summary" value={h.claimSummary} />
      <DetailField label="Falsifier" value={h.falsifier} />
      <DetailField label="Case set" value={h.casePair} />
      <DetailField label="Pair logic" value={h.pairLogic} />
      {h.phaseAVerdict && (
        <DetailField label="Phase A verdict" value={h.phaseAVerdict} highlight />
      )}
      {h.criticalTBD && (
        <DetailField label="Critical TBD" value={h.criticalTBD} warning />
      )}
      {h.killedDate && (
        <DetailField label="Killed date" value={h.killedDate} />
      )}
      {h.supersededBy && (
        <DetailField label="Superseded by" value={h.supersededBy} mono />
      )}
      {h.relatedHypotheses && h.relatedHypotheses.length > 0 && (
        <DetailField
          label="Related"
          value={h.relatedHypotheses.join(' · ')}
          mono
        />
      )}
      <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginTop: 12 }}>
        Group: {GROUP_LABEL[h.group] || h.group} · Created: {h.created}
      </div>
    </div>
  );
}

function DetailField({ label, value, mono, highlight, warning }) {
  if (!value) return null;
  const valueColor = warning ? C.terra : highlight ? C.gold : C.text;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 12,
          color: valueColor,
          lineHeight: 1.5,
          fontFamily: mono ? F.mono : F.sans,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ─── Shared atoms ────────────────────────────────────────────────────
function SectionHeading({ number, label, sublabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
      <span style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>{number}</span>
      <span style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 400, color: C.text }}>{label}</span>
      {sublabel && (
        <span style={{ fontSize: 10, color: C.static, fontFamily: F.mono, letterSpacing: '0.04em', marginLeft: 6 }}>
          {sublabel}
        </span>
      )}
    </div>
  );
}

function StatusBadge({ color, label, filled }) {
  return (
    <span
      style={{
        fontSize: 9,
        fontFamily: F.mono,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '3px 8px',
        borderRadius: 3,
        border: `1px solid ${color}`,
        background: filled ? hexAlpha(color, 0.1) : 'transparent',
        color: color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function hexAlpha(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
